import RequestError from './HttpError';

export type Map = Record<string, string | boolean | number>;

type FetchFunction = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

const stringifyMap = (map: Map): Record<string, string> => Object.entries(map)
  .map(([key, value]) => [key, `${<string>value}`])
  .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

export default class HttpClient {
  private apiUri: string;
  private defaultHeaders: Map;
  private altfetch?: string;

  public constructor(apiUri: string, defaultHeaders: Map = {}, altfetch?: string) {
    this.apiUri = apiUri;
    this.defaultHeaders = defaultHeaders;
    this.altfetch = altfetch;
  }

  public async get<TResponse, TError>(path: string, query: Map = {}, headers: Map = {}): Promise<TResponse> {
    const headersExtended = {
      Accept: 'application/json; charset=utf-8',
      ...headers,
    };
    return this.fetch<undefined, TResponse, TError>('GET', path, query, headersExtended);
  }

  public async post<TBody, TError>(path: string, body: TBody, query: Map = {}, headers: Map = {}): Promise<void> {
    const headersExtended = {
      'Content-Type': 'application/json; charset=utf-8',
      ...headers,
    };
    return this.fetch<TBody, void, TError>('POST', path, query, headersExtended, body);
  }

  private async fetch<TBody, TResponse, TError>(
    method: 'POST' | 'GET',
    path: string,
    query: Map = {},
    headers: Map = {},
    body?: TBody,
  ): Promise<TResponse> {
    const params = new URLSearchParams(
      Object.entries(query).map(
        ([key, value]) => [key, value.toString()],
      ),
    );
    const qs = params.toString();
    const qsPrefixed = qs ? `?${qs}` : '';
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const afetch: FetchFunction = (window[this.altfetch] ?? fetch) as FetchFunction;
    const response = await afetch(`${this.apiUri}${path}${qsPrefixed}`, {
      method,
      headers: stringifyMap({
        ...this.defaultHeaders,
        ...headers,
      }),
      mode: 'cors',
      credentials: 'same-origin',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) {
      const { status, statusText } = response;
      const errorBody = await response.json() as TError;
      throw new RequestError(status, statusText, errorBody);
    }
    return method === 'GET' ? response.json() : undefined;
  }
}
